import { useMemo } from 'react'
import { Camera, Globe, Loader2 } from 'lucide-react'
import {
  useCaptureFlow,
  type PresentCaptureFlowPayload,
} from '@/components/chat/capture-flow-context'

interface PresentCaptureFlowToolMessageProps {
  part: any
}

function readPayload(part: any): PresentCaptureFlowPayload | null {
  // During streaming the AI SDK exposes tool input before execute() returns.
  // That input contains agent frames but not the canonical test evidence added
  // to the output, so rendering it causes a brief 1/2/3 card before the real
  // evidence card replaces it. Wait for the output and render only once.
  if (part?.state && part.state !== 'output-available') return null
  if (
    part?.toolInvocation?.state &&
    part.toolInvocation.state !== 'result'
  ) {
    return null
  }

  const tryParse = (raw: unknown) => {
    if (raw == null) return null
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw)
      } catch {
        return null
      }
    }
    return raw as Record<string, unknown>
  }

  const candidate =
    tryParse(part?.output) ??
    tryParse(part?.toolInvocation?.result) ??
    tryParse(part?.input) ??
    tryParse(part?.toolInvocation?.args)

  if (!candidate || typeof candidate !== 'object') return null
  const data = candidate as Record<string, any>
  if (
    typeof data.rationale !== 'string' ||
    typeof data.testId !== 'string' ||
    !Array.isArray(data.frames)
  ) {
    return null
  }
  return {
    rationale: data.rationale,
    testId: data.testId,
    title: typeof data.title === 'string' ? data.title : undefined,
    entryUrl: typeof data.entryUrl === 'string' ? data.entryUrl : undefined,
    frames: data.frames,
    instructions: typeof data.instructions === 'string' ? data.instructions : undefined,
    fallbackInstructions:
      typeof data.fallbackInstructions === 'string'
        ? data.fallbackInstructions
        : '',
  }
}

export function PresentCaptureFlowToolMessage({
  part,
}: PresentCaptureFlowToolMessageProps) {
  const flow = useCaptureFlow()
  const payload = useMemo(() => readPayload(part), [part])

  const toolCallId: string =
    part?.toolCallId ?? part?.toolInvocation?.toolCallId ?? ''

  if (!payload) return null

  const extensionInstalled = flow?.extensionInstalled ?? null
  const captureStartingId = flow?.captureStartingId ?? null
  const isStarting = captureStartingId === toolCallId
  const disabled = !flow || !!captureStartingId

  return (
    <div className='mb-2.5 w-full overflow-hidden rounded-md border border-border bg-card last:mb-0'>
      <div className='flex items-start justify-between gap-3 border-b border-border-faint px-4 py-2.5'>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <Globe className='h-3.5 w-3.5 shrink-0 text-text-secondary' />
            <div className='text-[12px] leading-[1.45] font-semibold break-words text-text-secondary'>
              Capture evidence in browser
            </div>
          </div>
          <div className='mt-1 text-[11px] leading-[1.45] text-text-body'>
            {payload.rationale}
          </div>
        </div>
      </div>
      <div className='space-y-3 px-4 py-3'>
        {payload.entryUrl && (
          <div className='rounded-md bg-surface-subtle px-3 py-2'>
            <div className='text-[10px] font-semibold tracking-[0.08em] text-text-subtle uppercase'>
              Start from
            </div>
            <a
              href={payload.entryUrl}
              target='_blank'
              rel='noreferrer'
              className='mt-1 block text-[11px] leading-[1.45] break-all text-cobalt-strong hover:underline'
            >
              {payload.entryUrl}
            </a>
          </div>
        )}
        {extensionInstalled === false && payload.fallbackInstructions && (
          <div className='rounded-md bg-surface-subtle px-3 py-2 text-[11px] leading-[1.45] text-text-body'>
            {payload.fallbackInstructions}
          </div>
        )}
        {payload.instructions ? (
          <div className='rounded-md bg-surface-subtle px-3 py-2'>
            <div className='text-[10px] font-semibold tracking-[0.08em] text-text-subtle uppercase'>
              What to provide
            </div>
            <p className='mt-1.5 whitespace-pre-wrap text-[11px] leading-[1.45] text-text-body'>
              {payload.instructions}
            </p>
          </div>
        ) : (
          <div className='rounded-md bg-surface-subtle px-3 py-1'>
            {payload.frames.map((frame, index) => (
              <div key={`${frame.label}-${index}`} className='py-2'>
                <div className='text-[11px] font-semibold text-text-secondary'>
                  {index + 1}. {frame.label}
                </div>
                <div className='mt-1 text-[11px] leading-[1.45] text-text-body'>
                  {frame.instructions}
                </div>
                {frame.annotations && frame.annotations.length > 0 && (
                  <ul className='mt-2 space-y-1 pl-4 text-[11px] leading-[1.35] text-text-subtle'>
                    {frame.annotations.map((annotation) => (
                      <li key={annotation} className='list-disc'>
                        {annotation}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className='flex items-center justify-end gap-2 px-3 py-2'>
        <button
          type='button'
          onClick={() => flow?.startCapture(payload, toolCallId)}
          disabled={disabled}
          className='inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {isStarting ? (
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
          ) : (
            <Camera className='h-3.5 w-3.5' />
          )}
          {isStarting
            ? 'Starting capture...'
            : extensionInstalled === false
              ? 'Install Screenata Capture'
              : 'Capture Screenshots'}
        </button>
      </div>
    </div>
  )
}
